import { SITE_URL } from './seo';
import { isKayiComNativeApp } from './androidApp';

const PENDING_KEY = 'google_login_pending';
const OAUTH_RETURN_KEY = 'kayi_oauth_return';
const processedCodes = new Set();
let completingCode = null;

/** Canonical Google OAuth redirect — must match Google Cloud Console exactly. */
export const getGoogleRedirectUri = (settings) => {
  const fromSettings = String(settings?.google_redirect_uri || '').trim();
  if (fromSettings) return fromSettings.replace(/\/+$/, '');
  return `${SITE_URL}/login`;
};

/** Production Web OAuth client (Admin Settings). Safe to ship — not a secret. */
export const KAYICOM_GOOGLE_WEB_CLIENT_ID =
  '447796799261-d8ljf1mh2c6c8b3r7a196k5nbrr8map6.apps.googleusercontent.com';

export const getGoogleWebClientId = (settings) => {
  const fromSettings = String(settings?.google_client_id || '').trim();
  if (fromSettings) return fromSettings;
  return KAYICOM_GOOGLE_WEB_CLIENT_ID;
};

export const saveGoogleLoginPending = ({ state, redirectUri, fromApp = false }) => {
  const payload = JSON.stringify({
    state,
    redirectUri,
    fromApp: Boolean(fromApp),
    createdAt: Date.now(),
  });
  try {
    localStorage.setItem(PENDING_KEY, payload);
  } catch (e) {
    // ignore
  }
  try {
    sessionStorage.setItem(PENDING_KEY, payload);
  } catch (e) {
    // ignore
  }
};

export const readGoogleLoginPending = () => {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(PENDING_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.state) return parsed;
    } catch (e) {
      // try next store
    }
  }
  return null;
};

export const clearGoogleLoginPending = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // ignore
  }
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // ignore
  }
};

/** True when the Android shell can show the device Google account picker. */
export const canUseNativeGoogleSignIn = () => {
  if (!isKayiComNativeApp()) return false;
  try {
    return Boolean(
      window.KayiNative
      && typeof window.KayiNative.signInWithGoogle === 'function'
    );
  } catch {
    return false;
  }
};

/**
 * Native Credential Manager flow — uses Google accounts already on the phone.
 * Resolves { idToken } or rejects with Error / { cancelled: true }.
 */
export const nativeGoogleSignIn = (webClientId) => {
  const clientId = String(webClientId || '').trim();
  if (!clientId) {
    return Promise.reject(new Error('Google client ID missing'));
  }
  if (!canUseNativeGoogleSignIn()) {
    return Promise.reject(new Error('Native Google Sign-In unavailable'));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Google Sign-In timed out'));
    }, 120000);

    const cleanup = () => {
      clearTimeout(timeout);
      try {
        delete window.__kayiGoogleSignInDone;
      } catch {
        window.__kayiGoogleSignInDone = undefined;
      }
    };

    window.__kayiGoogleSignInDone = (result) => {
      cleanup();
      if (!result) {
        reject(new Error('Google Sign-In failed'));
        return;
      }
      if (result.ok && result.idToken) {
        resolve({ idToken: String(result.idToken) });
        return;
      }
      if (result.cancelled) {
        const err = new Error('cancelled');
        err.cancelled = true;
        reject(err);
        return;
      }
      const err = new Error(result.error || 'Google Sign-In failed');
      err.fallback = result.fallback !== false;
      reject(err);
    };

    try {
      window.KayiNative.signInWithGoogle(clientId);
    } catch (e) {
      cleanup();
      reject(e instanceof Error ? e : new Error('Google Sign-In failed'));
    }
  });
};

/**
 * Open Google OAuth.
 * In the KayiCom Android app: Chrome Custom Tabs (Google blocks OAuth inside WebView → 400).
 * After success, App Links return to https://kayicom.com/login?code=...
 */
export const openGoogleAuthUrl = (authUrl) => {
  if (!authUrl) throw new Error('Missing auth URL');
  if (
    isKayiComNativeApp()
    && window.KayiNative
    && typeof window.KayiNative.openInChrome === 'function'
  ) {
    window.KayiNative.openInChrome(authUrl);
    return { via: 'custom_tabs' };
  }
  window.location.assign(authUrl);
  return { via: 'browser' };
};

/** If OAuth finished in Chrome, bounce once into the installed app. */
export const maybeBounceOAuthToNativeApp = () => {
  if (typeof window === 'undefined') return false;
  if (isKayiComNativeApp()) return false;
  const pending = readGoogleLoginPending();
  if (!pending?.fromApp) return false;
  try {
    if (sessionStorage.getItem('kayi_oauth_bounce_tried') === '1') return false;
    sessionStorage.setItem('kayi_oauth_bounce_tried', '1');
  } catch {
    // continue
  }
  const search = window.location.search || '';
  const intent =
    `intent://login${search}#Intent;scheme=https;host=kayicom.com;package=com.kayicom.app;`
    + `S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`;
  window.location.href = intent;
  return true;
};

/**
 * Exchange a Google auth code once (deduped). Used by LoginPage and the native
 * App Link bridge so Custom Tabs return always finishes login.
 */
export const completeGoogleOAuthWithCode = async ({
  code,
  state,
  settings,
  postCode,
}) => {
  const authCode = String(code || '').trim();
  if (!authCode) return { ok: false, reason: 'missing_code' };
  if (processedCodes.has(authCode) || completingCode === authCode) {
    return { ok: false, reason: 'duplicate' };
  }
  completingCode = authCode;
  processedCodes.add(authCode);
  try {
    const pending = readGoogleLoginPending();
    if (pending?.state && state && pending.state !== state) {
      clearGoogleLoginPending();
      processedCodes.delete(authCode);
      return { ok: false, reason: 'state_mismatch' };
    }
    const redirectUri =
      (pending && pending.redirectUri) || getGoogleRedirectUri(settings);
    const response = await postCode({
      code: authCode,
      redirect_uri: redirectUri,
    });
    clearGoogleLoginPending();
    try {
      sessionStorage.removeItem(OAUTH_RETURN_KEY);
    } catch {
      // ignore
    }
    return { ok: true, user: response };
  } catch (error) {
    processedCodes.delete(authCode);
    throw error;
  } finally {
    if (completingCode === authCode) completingCode = null;
  }
};

/**
 * Native App Link injects window.__kayiHandleGoogleOAuthReturn({ code, state }).
 * Also drains a sessionStorage stash if the SPA was not ready yet.
 */
export const installNativeGoogleOAuthBridge = ({ postCode, onSuccess, onError, settings }) => {
  if (typeof window === 'undefined') return () => {};

  const run = async (payload) => {
    const code = payload?.code;
    const state = payload?.state;
    if (!code) return;
    try {
      const result = await completeGoogleOAuthWithCode({
        code,
        state,
        settings,
        postCode,
      });
      if (result.ok && result.user) {
        onSuccess(result.user);
        return;
      }
      if (result.reason === 'state_mismatch') {
        onError(new Error('Login session expired. Please try again.'));
      }
      // duplicate / busy → ignore
    } catch (error) {
      onError(error);
    }
  };

  window.__kayiHandleGoogleOAuthReturn = run;

  try {
    const raw = sessionStorage.getItem(OAUTH_RETURN_KEY);
    if (raw) {
      sessionStorage.removeItem(OAUTH_RETURN_KEY);
      const parsed = JSON.parse(raw);
      if (parsed?.code) run(parsed);
    }
  } catch {
    // ignore
  }

  return () => {
    try {
      if (window.__kayiHandleGoogleOAuthReturn === run) {
        delete window.__kayiHandleGoogleOAuthReturn;
      }
    } catch {
      // ignore
    }
  };
};

/** Stash OAuth return if React handler is not mounted yet (native inject fallback). */
export const stashGoogleOAuthReturn = (payload) => {
  try {
    sessionStorage.setItem(OAUTH_RETURN_KEY, JSON.stringify(payload || {}));
  } catch {
    // ignore
  }
};
